import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger } from
"@/components/ui/accordion";
import {
  Search,
  Shield,
  Brain,
  Link2,
  Users,
  TrendingUp,
  Zap,
  Sparkles,
  Globe,
  Clock,
  ArrowRight,
  CheckCircle2,
  BarChart3,
  Target,
  Eye,
  FileText,
  Key,
  Layers,
  Quote,
  MessageSquare,
  PenTool } from
"lucide-react";
import EmailGateModal from "@/components/EmailGateModal";
import WaitlistBanner from "@/components/WaitlistBanner";
import WaitlistModal from "@/components/WaitlistModal";

/* ─── Data ─── */

const pillars = [
{ icon: Shield, num: "01", title: "SEO Foundation Audit", desc: "Deep analysis of on-page elements, technical health, meta tags, and content quality to uncover what's holding your rankings back." },
{ icon: Key, num: "02", title: "Keyword Analysis", desc: "Comprehensive keyword density, placement, and intent analysis to ensure your content targets the right terms in the right places." },
{ icon: Brain, num: "03", title: "AI Visibility Engine", desc: "Measure how well your site surfaces in ChatGPT, Gemini, AI Overviews, and other AI-powered search experiences." },
{ icon: Link2, num: "04", title: "Backlink Intelligence", desc: "Evaluate your authority profile, toxic links, and domain trust signals that search engines use to rank your pages." },
{ icon: Users, num: "05", title: "Competitor Intelligence", desc: "Side-by-side gap analysis reveals exactly where competitors outperform you — and how to close the gap." },
{ icon: TrendingUp, num: "06", title: "Ranking Predictor", desc: "Data-driven estimation of your ranking potential based on current signals, content strength, and authority metrics." },
{ icon: Zap, num: "07", title: "Smart Action Plan", desc: "A prioritized, step-by-step roadmap of improvements sorted by impact — so you know exactly what to fix first." }];

const comingSoonPillars = [
{ icon: MessageSquare, num: "08", title: "AI Agent Mode", desc: "Chat with an AI assistant directly within your analysis to get deeper insights, tailored suggestions, and step-by-step guidance.", premium: true },
{ icon: PenTool, num: "09", title: "Content Creation", desc: "Generate optimized titles, paragraphs, and content pieces based on your page data and keyword opportunities.", premium: true }];


const trust = [
{ icon: Sparkles, text: "AI-powered analysis" },
{ icon: Globe, text: "1 free analysis — no signup needed" },
{ icon: Clock, text: "Actionable insights in minutes" }];


const socialProof = [
{ value: "500+", label: "Websites Analyzed" },
{ value: "9", label: "Analysis Modules" },
{ value: "< 2 min", label: "Time to Insights" }];


const benefits = [
{
  icon: Eye,
  title: "Built for the AI Search Era",
  desc: "Traditional SEO tools ignore AI search. Unranked analyzes your visibility across ChatGPT, Gemini, and AI Overviews — the fastest-growing traffic sources on the web."
},
{
  icon: FileText,
  title: "Actionable, Not Just Data",
  desc: "We don't dump raw metrics on you. Every report includes a prioritized action plan sorted by impact, so you always know the highest-value fix to make next."
},
{
  icon: Target,
  title: "Know Your Competitors",
  desc: "See exactly which keywords your competitors rank for that you don't, where their backlinks come from, and what content gaps you can exploit to overtake them."
}];


const faqs = [
{
  q: "Is the SEO analysis really free?",
  a: "Yes — every visitor gets one completely free, comprehensive SEO and AI visibility analysis. No credit card, no signup required. Just paste your URL and get your report."
},
{
  q: "What does the AI visibility score measure?",
  a: "The AI visibility score evaluates how likely your website is to appear in AI-powered search results — including ChatGPT responses, Google AI Overviews, and Gemini recommendations. It analyzes structured data, content clarity, authority signals, and semantic relevance."
},
{
  q: "How is Unranked different from other SEO tools?",
  a: "Most SEO tools focus exclusively on traditional search rankings. Unranked combines classic SEO auditing with AI visibility analysis, competitor intelligence, and a prioritized action plan — giving you a complete picture of your search presence in both traditional and AI-powered search."
},
{
  q: "Do I need to create an account?",
  a: "Not for your first analysis. Simply enter your URL and email to receive your free report. If you'd like ongoing monitoring and additional analyses, you can join our early-access waitlist for priority access when we launch."
},
{
  q: "How long does the analysis take?",
  a: "Most analyses complete in under two minutes. Our AI engine crawls your site, evaluates over 50 ranking factors, benchmarks you against competitors, and generates a full report — all in real time."
},
{
  q: "What types of websites can I analyze?",
  a: "Unranked works with any publicly accessible website — from small business sites and blogs to large e-commerce stores and SaaS platforms. If search engines can crawl it, we can analyze it."
}];

const testimonials = [
{
  quote: "What stood out was the AI visibility analysis. Unranked Agency helped us understand how our content performs not only in Google, but also in AI-driven search.",
  author: "Bonuskartan Team",
  url: "https://bonuskartan.se/"
},
{
  quote: "Unranked Agency helped us understand how our brand and product pages perform in search. The analysis revealed clear opportunities to improve our content structure, SEO, and visibility in AI-driven search. The insights were practical and easy to act on.",
  author: "Cari Lettori"
}];



/* ─── Mock UI Components ─── */

const MockInput = () =>
<div className="flex items-center gap-2 rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm">
    <Globe className="h-3.5 w-3.5 text-muted-foreground" />
    <span className="text-muted-foreground">https://yoursite.com</span>
  </div>;


const MockProgress = () =>
<div className="space-y-2">
    <div className="h-2 w-full rounded-full bg-secondary">
      <div className="h-2 w-3/4 rounded-full bg-primary transition-all" />
    </div>
    <div className="flex justify-between text-[10px] text-muted-foreground">
      <span>Crawling pages…</span>
      <span>75%</span>
    </div>
  </div>;


const MockGauge = ({ score, label, color }: {score: number;label: string;color: string;}) => {
  const r = 28;
  const c = 2 * Math.PI * r;
  const offset = c - score / 100 * c;
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width="72" height="72" viewBox="0 0 72 72">
        <circle cx="36" cy="36" r={r} fill="none" stroke="hsl(var(--secondary))" strokeWidth="5" />
        <circle
          cx="36"
          cy="36"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="5"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 36 36)" />

        <text x="36" y="41" textAnchor="middle" fontSize="14" fontWeight="bold" className="fill-foreground">
          {score}
        </text>
      </svg>
      <span className="text-[10px] text-muted-foreground">{label}</span>
    </div>);

};

const MockDashboard = () =>
<div className="mx-auto max-w-3xl">
    {/* Browser chrome */}
    <div className="rounded-t-xl border border-border bg-card px-4 py-2.5 flex items-center gap-2">
      <div className="flex gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
        <div className="w-2.5 h-2.5 rounded-full bg-primary/60" />
      </div>
      <div className="flex-1 ml-3 rounded-md bg-secondary/70 px-3 py-1 text-xs text-muted-foreground">unrankedagency.com/report</div>
    </div>

    {/* Dashboard body */}
    <div className="rounded-b-xl border border-t-0 border-border bg-card/80 p-6 space-y-6">
      {/* Hero: Overall Score */}
      <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
        <MockGauge score={72} label="" color="hsl(var(--primary))" />
        <div className="text-center sm:text-left">
          <h3 className="text-sm font-semibold text-foreground">Overall Visibility</h3>
          <p className="text-xs font-medium text-success">Good</p>
          <div className="flex items-center gap-1 mt-1">
            <span className="text-[10px] text-muted-foreground border border-border rounded px-1.5 py-0.5">95% confidence</span>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1 font-mono">yoursite.com</p>
        </div>
      </div>

      {/* Metric Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
      { icon: "🔍", label: "On-Page SEO", score: 68 },
      { icon: "⚙️", label: "Technical SEO", score: 74 },
      { icon: "📄", label: "Content Quality", score: 61 },
      { icon: "🤖", label: "AI Visibility", score: 58 }].
      map((m) =>
      <div key={m.label} className="rounded-lg border border-border bg-card p-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs">{m.icon}</span>
              <span className="text-sm font-bold text-foreground">{m.score}</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-secondary mb-1.5">
              <div
            className={`h-1.5 rounded-full transition-all ${m.score >= 70 ? "bg-success" : m.score >= 40 ? "bg-warning" : "bg-destructive"}`}
            style={{ width: `${m.score}%` }} />
          
            </div>
            <p className="text-[10px] text-muted-foreground">{m.label}</p>
          </div>
      )}
      </div>

      {/* Tab bar */}
      <div className="flex flex-wrap justify-start gap-1 rounded-lg bg-secondary/50 p-1 text-[11px] h-auto">
        {["SEO Audit", "Keywords", "AI Visibility", "Link Intel", "Competitors", "Ranking", "Action Plan"].map(
        (tab, i) =>
        <div
          key={tab}
          className={`text-center rounded-md px-2.5 py-1.5 transition-colors whitespace-nowrap ${
          i === 0 ?
          "bg-primary text-primary-foreground font-medium" :
          "text-muted-foreground hover:text-foreground"}`
          }>
              {tab}
            </div>
      )}
      </div>

      {/* Key metrics snapshot */}
      <div className="grid grid-cols-3 gap-3">
        {[
      { label: "Ranking Estimate", val: "#8–15", subtitle: "Current position" },
      { label: "AI Mention Potential", val: "120–180/mo", subtitle: "Estimated mentions" },
      { label: "Action Items", val: "7 fixes", subtitle: "Ready to implement" }].
      map((m) =>
      <div key={m.label} className="rounded-lg border border-border bg-secondary/30 p-3 text-center">
            <p className="text-[10px] text-muted-foreground">{m.label}</p>
            <p className="text-lg font-bold text-foreground">{m.val}</p>
            <p className="text-[10px] text-muted-foreground/70">{m.subtitle}</p>
          </div>
      )}
      </div>

      {/* Mini action items preview */}
      <div className="space-y-2">
        <p className="text-[11px] font-medium text-muted-foreground">Top Action Items</p>
        <div className="rounded-lg border border-border bg-secondary/20 p-2.5 flex items-center gap-2">
          <span className="shrink-0 rounded-full bg-destructive/20 text-destructive text-[10px] font-semibold px-2 py-0.5">High</span>
          <span className="text-[11px] text-foreground">Add structured data for FAQ schema</span>
        </div>
        <div className="rounded-lg border border-border bg-secondary/20 p-2.5 flex items-center gap-2">
          <span className="shrink-0 rounded-full bg-accent/20 text-accent text-[10px] font-semibold px-2 py-0.5">Medium</span>
          <span className="text-[11px] text-foreground">Improve internal linking to key pages</span>
        </div>
      </div>
    </div>
  </div>;


/* ─── Page ─── */

const Index = () => {
  const [url, setUrl] = useState("");
  const [showGate, setShowGate] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const navigate = useNavigate();

  const handleAnalyze = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;
    setShowGate(true);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* ── Nav ── */}
      <nav className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-md">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <a href="#" className="flex items-center gap-2">
            <img src="/Unranked-agency-logo.png" alt="Unranked Agency" className="h-5 sm:h-6 w-auto object-contain max-w-[140px] sm:max-w-none" />
          </a>

          <div className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#how-it-works" className="hover:text-foreground transition-colors">How It Works</a>
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
          </div>

          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" asChild>
              <Link to="/login">Login</Link>
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowWaitlist(true)}>
              Join Waitlist
              <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </nav>

      <main>
        {/* ── Hero ── */}
        <header className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(160_84%_45%/0.08),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,hsl(265_85%_60%/0.06),transparent_50%)]" />

          <div className="relative z-10 container mx-auto px-6 pt-20 pb-24 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border bg-secondary/50 text-sm text-muted-foreground mb-8">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              Free AI-Powered SEO &amp; Visibility Analysis
            </div>

            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-[1.1]">
              Discover Why Your Website{" "}
              <span className="gradient-text">Isn't Ranking</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground mb-12 max-w-2xl mx-auto">
              Get a comprehensive SEO audit and AI visibility analysis with a prioritized action plan
              — covering backlinks, competitors, and ranking potential. Paste your URL and get your
              free report in minutes.
            </p>

            <form
              onSubmit={handleAnalyze}
              className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">

              <div className="relative flex-1">
                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="url"
                  placeholder="https://example.com"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="h-14 pl-11 pr-4 bg-secondary/50 border-border text-base rounded-xl focus:ring-primary"
                  required
                  aria-label="Enter your website URL for a free SEO analysis" />

              </div>
              <Button
                type="submit"
                size="lg"
                className="h-14 px-8 rounded-xl text-base font-semibold glow-primary">

                Analyze My Site
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </form>

            <div className="flex flex-wrap items-center justify-center gap-6 mt-8">
              {trust.map((t) =>
              <div key={t.text} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <t.icon className="w-4 h-4 text-primary" />
                  {t.text}
                </div>
              )}
            </div>
          </div>
        </header>



        {/* ── How It Works ── */}
        <section id="how-it-works" className="container mx-auto px-6 py-20" aria-labelledby="how-heading">
          <div className="text-center mb-14">
            <h2 id="how-heading" className="text-3xl md:text-4xl font-bold mb-3">
              How It Works
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              From URL to actionable SEO insights in three simple steps.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto relative">

            {/* Step 1 */}
            <div className="relative text-center space-y-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold relative z-10">
                1
              </div>
              <h3 className="font-semibold text-foreground text-lg">Paste Your URL</h3>
              <p className="text-sm text-muted-foreground">
                Enter any website address into the analyzer — no account or installation needed.
              </p>
              <div className="mx-auto max-w-[220px]">
                <MockInput />
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative text-center space-y-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold relative z-10">
                2
              </div>
              <h3 className="font-semibold text-foreground text-lg">AI Analyzes Your Site</h3>
              <p className="text-sm text-muted-foreground">
                Our engine crawls your pages, evaluates 50+ ranking factors, and benchmarks you against competitors.
              </p>
              <div className="mx-auto max-w-[220px]">
                <MockProgress />
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative text-center space-y-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold relative z-10">
                3
              </div>
              <h3 className="font-semibold text-foreground text-lg">Get Your Full Report</h3>
              <p className="text-sm text-muted-foreground">
                Receive a detailed SEO and AI visibility report with a prioritized action plan you can act on immediately.
              </p>
              <div className="mx-auto max-w-[220px] flex justify-center gap-3">
                <MockGauge score={72} label="SEO" color="hsl(var(--primary))" />
                <MockGauge score={58} label="AI" color="hsl(var(--accent))" />
              </div>
            </div>
          </div>
        </section>

        {/* ── Platform Preview ── */}
        <section className="container mx-auto px-6 py-16" aria-label="Platform preview">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">See What You'll Get</h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              A complete SEO dashboard — scores, AI visibility metrics, competitor benchmarks, and your personalized action plan.
            </p>
          </div>
          <MockDashboard />
        </section>

        {/* ── Features Grid ── */}
        <section id="features" className="container mx-auto px-6 py-20" aria-labelledby="features-heading">
          <div className="text-center mb-12">
            <h2 id="features-heading" className="text-3xl md:text-4xl font-bold mb-3">
              9 Modules. One Complete Audit.
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Every angle of your search engine and AI visibility analyzed, scored, and turned into action.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {pillars.map((p) =>
            <article
              key={p.num}
              className="group p-6 rounded-xl bg-card border border-border hover:border-primary/30 transition-all duration-300">

                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <p.icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">{p.num}</span>
                </div>
                <h3 className="font-semibold text-foreground mb-1">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </article>
            )}
            {comingSoonPillars.map((p) =>
            <article
              key={p.num}
              className="group p-6 rounded-xl bg-card border border-dashed border-border/60 opacity-70 transition-all duration-300 relative">

                <div className="absolute top-3 right-3 flex gap-1.5">
                  <span className="px-2 py-0.5 rounded-full bg-primary/20 text-primary text-[10px] font-semibold">Coming Soon</span>
                  <span className="px-2 py-0.5 rounded-full bg-accent text-accent-foreground text-[10px] font-semibold">Premium</span>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <p.icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">{p.num}</span>
                </div>
                <h3 className="font-semibold text-foreground mb-1">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </article>
            )}
          </div>
        </section>

        {/* ── Why Unranked ── */}
        <section className="container mx-auto px-6 py-20" aria-labelledby="benefits-heading">
          <div className="text-center mb-14">
            <h2 id="benefits-heading" className="text-3xl md:text-4xl font-bold mb-3">
              Why Unranked?
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              A smarter SEO audit built for how search actually works today.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {benefits.map((b) =>
            <div key={b.title} className="space-y-3">
                <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center">
                  <b.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground text-lg">{b.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
              </div>
            )}
          </div>
        </section>

        {/* ── Testimonials ── */}
        <section className="container mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">What Our Users Say</h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Real feedback from teams using Unranked Agency to improve their search visibility.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {testimonials.map((t, i) =>
            <div key={i} className="bg-card border border-border rounded-xl p-6 flex flex-col gap-4">
                <Quote className="w-8 h-8 text-primary opacity-60" />
                <p className="text-muted-foreground italic leading-relaxed">"{t.quote}"</p>
                <div className="mt-auto flex items-center justify-between">
                  <p className="text-foreground font-semibold">— {t.author}</p>
                  {t.url &&
                <a href={t.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                      {t.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    </a>
                }
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ── FAQ ── */}
        <section id="faq" className="container mx-auto px-6 py-20" aria-labelledby="faq-heading">
          <div className="text-center mb-12">
            <h2 id="faq-heading" className="text-3xl md:text-4xl font-bold mb-3">
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Everything you need to know about our free SEO and AI visibility analysis.
            </p>
          </div>

          <Accordion type="single" collapsible className="max-w-2xl mx-auto">
            {faqs.map((f, i) =>
            <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-foreground">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </section>

        {/* ── Waitlist CTA ── */}
        <WaitlistBanner variant="full" onJoinClick={() => setShowWaitlist(true)} />
      </main>

      {/* ── Footer ── */}
      <footer className="border-t border-border py-10">
        <div className="container mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6 text-sm text-muted-foreground">
          <div className="flex flex-col items-center gap-1">
            <img src="/Unranked-agency-logo.png" alt="Unranked Agency" className="h-6 w-auto object-contain" />
            <span className="text-muted-foreground">AI-powered SEO &amp; visibility analysis</span>
          </div>

          <div className="flex items-center gap-6">
            <a href="#how-it-works" className="hover:text-foreground transition-colors">How It Works</a>
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
            <Link to="/login" className="hover:text-foreground transition-colors">Login</Link>
            <a href="/sitemap.xml" className="hover:text-foreground transition-colors">Sitemap</a>
          </div>

          <span>© {new Date().getFullYear()} Unranked Agency. All rights reserved.</span>
        </div>
      </footer>

      <EmailGateModal open={showGate} onOpenChange={setShowGate} url={url.trim()} />
      <WaitlistModal open={showWaitlist} onOpenChange={setShowWaitlist} />
    </div>);

};

export default Index;